// CMN Tracking — Certificate of Medical Necessity management
import { useState, useEffect, useCallback } from 'react';
import { fileName } from '../../lib/documents/fileNames';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { uploadAndLinkDoc } from '../../lib/driveDocs';
import { loadFeeSchedule } from '../../lib/dmeWorkflow';
import { format } from 'date-fns';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import PhysicianPicker from '../../components/directory/PhysicianPicker';
import { getCareTeamDetailed, getPhysician, fmtPhone } from '../../lib/directory';
import { isClosedClient } from '../../lib/serviceModel';

const STATUS_CFG = {
  pending:   { label: 'Pending',   color: 'var(--warn)',    bg: 'var(--warn-bg)' },
  sent:      { label: 'Sent',      color: 'var(--info)',    bg: 'var(--info-bg)' },
  signed:    { label: 'Signed',    color: 'var(--success)', bg: 'var(--success-bg)' },
  expired:   { label: 'Expired',   color: 'var(--danger)',  bg: 'var(--danger-bg)' },
  cancelled: { label: 'Cancelled', color: 'var(--text3)',   bg: 'var(--bg2)' },
};


const QUALIFYING_DX = ['Cerebral palsy','Spina bifida','Autism spectrum disorder','Neurogenic bladder','Spinal cord injury','Intellectual disability',"Crohn's disease",'Ulcerative colitis','Bowel/bladder dysfunction','Other'];

function ModalBackdrop({ onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: 620, boxShadow: '0 24px 48px rgba(0,0,0,0.25)', flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

export default function CmnTracking() {
  const [cmns, setCmns]           = useState([]);
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');

  const blankForm = {
    client_id: '', client_name: '', client_ref: '',
    product_category: 'incontinence', hcpcs_codes: [],
    physician_id: null, prescribing_physician: '', physician_npi: '', physician_phone: '',
    qualifying_diagnosis: '', icd10_code: '',
    ordered_date: '', sent_date: '', signed_date: '', expiry_date: '',
    status: 'pending', notes: '', document_url: '',
  };
  const [form, setForm] = useState(blankForm);
  const [schedule, setSchedule] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { profile } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    const [cmnRes, clRes, sched] = await Promise.all([
      supabase.from('dme_cmns').select('*').order('created_at', { ascending: false }),
      supabase.from('clients').select('id, client_id, first_name, last_name, drive_folder_id, current_status').contains('service_lines', ['dme']).order('last_name'),
      loadFeeSchedule(),
    ]);
    // Closed clients (discharged, on hold, …) are out of CMN monitoring.
    const closedIds = new Set((clRes.data || []).filter(isClosedClient).map(c => c.id));
    setCmns((cmnRes.data || []).filter(c => !closedIds.has(c.client_id)));
    setClients((clRes.data || []).filter(c => !isClosedClient(c)));
    setSchedule(sched);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const [pickedDoc, setPickedDoc] = useState(null);
  const docName = (p) => `${p.first_name} ${p.last_name}`.trim() + (p.credentials ? `, ${p.credentials}` : '');
  const pickDoc = (p) => {
    setPickedDoc(p);
    setForm(f => ({ ...f, physician_id: p?.id || null, prescribing_physician: p ? docName(p) : '', physician_npi: p ? (p.npi || '') : '', physician_phone: p?.phone ? fmtPhone(p.phone) : '' }));
  };
  const handleClientChange = async (id) => {
    const c = clients.find(x => x.id === id);
    if (!c) { setForm(f => ({ ...f, client_id: '', client_name: '', client_ref: '' })); return; }
    setForm(f => ({ ...f, client_id: id, client_name: `${c.first_name} ${c.last_name}`, client_ref: c.client_id }));
    // the client's ordering physician (or PCP) from the directory
    const { data: team } = await getCareTeamDetailed(id);
    const doc = (team || []).find(m => m.role === 'ordering')?.physician || (team || []).find(m => m.role === 'pcp')?.physician || null;
    if (doc) pickDoc(doc);
  };

  const toggleCode = (code) => {
    setForm(f => ({
      ...f,
      hcpcs_codes: f.hcpcs_codes.includes(code)
        ? f.hcpcs_codes.filter(c => c !== code)
        : [...f.hcpcs_codes, code],
    }));
  };

  const openNew  = () => { setEditing(null); setForm(blankForm); setPickedDoc(null); setSaveError(''); setShowModal(true); };
  const openEdit = (c) => {
    setEditing(c); setForm({ ...c }); setSaveError(''); setShowModal(true);
    setPickedDoc(null);
    if (c.physician_id) getPhysician(c.physician_id).then(({ data }) => setPickedDoc(data || null));
  };

  const handleSave = async () => {
    if (!form.client_id)              { setSaveError('Please select a patient.'); return; }
    if (form.status === 'signed' && !form.document_url) { setSaveError('A CMN can only be marked signed with the signed document attached.'); return; }
    if (!form.prescribing_physician)  { setSaveError('Prescribing physician is required.'); return; }
    setSaving(true); setSaveError('');
    const payload = { ...form, updated_at: new Date().toISOString() };
    ['ordered_date','sent_date','signed_date','expiry_date'].forEach(k => { if (!payload[k]) payload[k] = null; });
    const { error } = editing
      ? await supabase.from('dme_cmns').update(payload).eq('id', editing.id)
      : await supabase.from('dme_cmns').insert(payload);
    if (error) { setSaveError(error.message); setSaving(false); return; }
    setShowModal(false);
    load();
    setSaving(false);
  };

  const handleUpdateStatus = async (id, status) => {
    const extra = {};
    if (status === 'signed') {
      const cmn = cmns.find(c => c.id === id);
      if (!cmn?.document_url) { openEdit(cmn); setSaveError('Attach the signed CMN before marking it signed.'); return; }
      extra.signed_date = new Date().toISOString().split('T')[0];
    }
    if (status === 'sent')   extra.sent_date   = new Date().toISOString().split('T')[0];
    await supabase.from('dme_cmns').update({ status, ...extra, updated_at: new Date().toISOString() }).eq('id', id);
    load();
  };

  const handleCmnFile = async (e) => {
    const file = e.target.files?.[0];
    const c = clients.find(x => x.id === form.client_id);
    if (!file || !c) { setSaveError('Select the client before attaching the CMN.'); return; }
    setUploading(true); setSaveError('');
    try {
      const url = await uploadAndLinkDoc({
        blob: file, contentType: file.type,
        filename: fileName([c.client_id, 'CMN', (form.hcpcs_codes || []).join(' ') || form.product_category, new Date().toISOString().slice(0, 10)], (file.name.match(/\.([a-z0-9]+)$/i)?.[1] || 'pdf')),
        clientCode: c.client_id, clientUuid: c.id, documentType: 'DME CMN', subfolder: 'DME',
        notes: form.prescribing_physician ? `Signed by ${form.prescribing_physician}` : null,
        profile, existingFolderId: c.drive_folder_id,
      });
      setForm(f => ({ ...f, document_url: url }));
    } catch (err) { setSaveError(err.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const filtered = cmns.filter(c => {
    const q = search.toLowerCase();
    if (q && !c.client_name?.toLowerCase().includes(q) && !c.prescribing_physician?.toLowerCase().includes(q)) return false;
    if (filterStatus && c.status !== filterStatus) return false;
    return true;
  });

  const counts = {
    pending:  cmns.filter(c => c.status === 'pending').length,
    sent:     cmns.filter(c => c.status === 'sent').length,
    signed:   cmns.filter(c => c.status === 'signed').length,
    expired:  cmns.filter(c => c.status === 'expired').length,
  };

  // Product category is a parallel identifier, not a status — neutral for all.
  const catColor = () => 'var(--text3)';
  const catBg    = () => 'var(--bg2)';
  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 4, display: 'block' };
  const FG = ({ label, children, half }) => (
    <div style={{ flex: half ? '1 1 calc(50% - 6px)' : '1 1 100%', minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  const columns = [
    {
      accessorKey: 'client_name', header: 'Patient', size: 160,
      meta: { mobile: 'primary' },
      cell: info => {
        const c = info.row.original;
        return (
          <div>
            {c.client_name}
            {c.client_ref && <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{c.client_ref}</div>}
          </div>
        );
      },
    },
    {
      id: 'category', accessorKey: 'product_category', header: 'Category', size: 110,
      meta: { mobile: 'secondary' },
      cell: info => {
        const cat = info.getValue();
        return <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 20, background: catBg(cat), color: catColor(cat), textTransform: 'capitalize' }}>{cat}</span>;
      },
    },
    {
      id: 'hcpcs_codes', header: 'HCPCS Codes', size: 150, enableSorting: false,
      meta: { mono: true, mobile: 'hidden' },
      cell: info => (info.row.original.hcpcs_codes || []).join(', ') || '—',
    },
    {
      accessorKey: 'prescribing_physician', header: 'Physician', size: 150,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ color: 'var(--text2)' }}>{info.getValue()}</span>,
    },
    {
      accessorKey: 'physician_npi', header: 'NPI', size: 110,
      meta: { mono: true, mobile: 'hidden' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'ordered_date', header: 'Ordered', size: 100,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue() ? format(new Date(info.getValue()), 'MMM d') : '—',
    },
    {
      accessorKey: 'sent_date', header: 'Sent', size: 100,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue() ? format(new Date(info.getValue()), 'MMM d') : '—',
    },
    {
      id: 'document', header: 'File', size: 60, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => info.row.original.document_url ? <a href={info.row.original.document_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11 }}>📄</a> : <span style={{ color: 'var(--text3)' }}>—</span>,
    },
    {
      accessorKey: 'signed_date', header: 'Signed', size: 100,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue() ? format(new Date(info.getValue()), 'MMM d') : '—',
    },
    {
      accessorKey: 'expiry_date', header: 'Expires', size: 120,
      meta: { mobile: 'secondary' },
      cell: info => {
        const c = info.row.original;
        const isPast = c.expiry_date && new Date(c.expiry_date) < new Date();
        return <span style={{ color: isPast ? 'var(--danger)' : 'var(--text3)' }}>{c.expiry_date ? format(new Date(c.expiry_date), 'MMM d, yyyy') : '—'}</span>;
      },
    },
    {
      accessorKey: 'status', header: 'Status', size: 110,
      meta: { mobile: 'primary' },
      cell: info => {
        const sc = STATUS_CFG[info.getValue()] || {};
        return <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: sc.bg, color: sc.color }}>{sc.label}</span>;
      },
    },
    {
      id: 'actions', header: '', size: 170, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const c = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => openEdit(c)}>Edit</button>
            {c.status === 'pending' && <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => handleUpdateStatus(c.id, 'sent')}>Mark Sent</button>}
            {c.status === 'sent' && <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => handleUpdateStatus(c.id, 'signed')}>Mark Signed</button>}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>CMN Tracking</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Certificate of Medical Necessity — all product lines</div>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ New CMN</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'Pending', val: counts.pending, tone: counts.pending > 0 ? 'warn' : 'neutral' },
          { label: 'Sent',    val: counts.sent,    tone: 'neutral' },
          { label: 'Signed',  val: counts.signed,  tone: 'neutral' },
          { label: 'Expired', val: counts.expired, tone: counts.expired > 0 ? 'alert' : 'neutral' },
        ].map(s => (
          <StatTile key={s.label} label={s.label} value={s.val} tone={s.tone} compact />
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patient or physician…"
          style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 300 }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, flex: '0 0 auto', width: 'auto' }}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="card">
        {!loading && filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '60px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>No CMNs yet</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Add the first Certificate of Medical Necessity to track physician sign-offs.</div>
            <button className="btn btn-primary btn-sm" onClick={openNew}>+ New CMN</button>
          </div>
        ) : (
          <DataTable
            tableId="dme-cmn"
            columns={columns}
            data={filtered}
            getRowId={c => c.id}
            loading={loading}
            emptyMessage="No CMNs match your filters."
            maxHeight="calc(100vh - 260px)"
          />
        )}
      </div>

      {showModal && (
        <ModalBackdrop onClose={() => setShowModal(false)}>
          <div style={{ padding: '18px 24px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{editing ? 'Edit CMN' : 'New CMN'}</div>
            <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
          </div>
          <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '70vh', overflowY: 'auto' }}>
            <FG label="Patient *">
              <select value={form.client_id} onChange={e => handleClientChange(e.target.value)} style={inputStyle}>
                <option value="">— Select patient —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.client_id})</option>)}
              </select>
            </FG>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <FG label="Product category" half>
                <select value={form.product_category} onChange={e => { set('product_category')(e.target.value); set('hcpcs_codes')([]); }} style={inputStyle}>
                  <option value="incontinence">Incontinence</option>
                  <option value="ostomy">Ostomy</option>
                  <option value="catheter">Catheter</option>
                </select>
              </FG>
              <FG label="Status" half>
                <select value={form.status} onChange={e => set('status')(e.target.value)} style={inputStyle}>
                  {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </FG>
            </div>

            <div>
              <label style={labelStyle}>HCPCS codes covered by this CMN</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {schedule.filter(x => x.category === form.product_category).map(x => x.hcpcs_code).map(code => {
                  const active = form.hcpcs_codes.includes(code);
                  return (
                    <button key={code} type="button" onClick={() => toggleCode(code)} style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                      background: active ? 'var(--accent-bg)' : 'var(--bg2)',
                      color: active ? 'var(--accent)' : 'var(--text3)',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                    }}>
                      {code}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <FG label="Physician — from the Provider Directory">
                <PhysicianPicker value={pickedDoc} onChange={pickDoc} placeholder="Search physician by name, NPI or practice…" />
              </FG>
              <FG label="Prescribing physician *">
                <input value={form.prescribing_physician} onChange={e => set('prescribing_physician')(e.target.value)} style={inputStyle} placeholder="Dr. Smith" disabled={!!pickedDoc} />
              </FG>
              <FG label="Physician NPI" half>
                <input value={form.physician_npi} onChange={e => set('physician_npi')(e.target.value)} style={inputStyle} disabled={!!pickedDoc} />
              </FG>
              <FG label="Physician phone" half>
                <input value={form.physician_phone} onChange={e => set('physician_phone')(e.target.value)} style={inputStyle} disabled={!!pickedDoc} />
              </FG>
              <FG label="Qualifying diagnosis" half>
                <select value={form.qualifying_diagnosis} onChange={e => set('qualifying_diagnosis')(e.target.value)} style={inputStyle}>
                  <option value="">— Select —</option>
                  {QUALIFYING_DX.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </FG>
              <FG label="ICD-10 code" half>
                <input value={form.icd10_code} onChange={e => set('icd10_code')(e.target.value)} style={inputStyle} placeholder="G82.20" />
              </FG>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <FG label="Ordered date" half>
                <input type="date" value={form.ordered_date} onChange={e => set('ordered_date')(e.target.value)} style={inputStyle} />
              </FG>
              <FG label="Sent to physician" half>
                <input type="date" value={form.sent_date} onChange={e => set('sent_date')(e.target.value)} style={inputStyle} />
              </FG>
              <FG label="Signed date" half>
                <input type="date" value={form.signed_date} onChange={e => set('signed_date')(e.target.value)} style={inputStyle} />
              </FG>
              <FG label="Expiry date" half>
                <input type="date" value={form.expiry_date} onChange={e => set('expiry_date')(e.target.value)} style={inputStyle} />
              </FG>
            </div>

            <div>
              <label style={labelStyle}>Signed CMN document{form.status === 'signed' && <span style={{ color: 'var(--danger)' }}> *</span>}</label>
              {form.document_url
                ? <div style={{ fontSize: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <a href={form.document_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>Filed in Drive → {form.client_ref}/DME</a>
                    <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => set('document_url')('')}>Replace</button>
                  </div>
                : <input type="file" accept="application/pdf,image/*" disabled={uploading || !form.client_id} onChange={handleCmnFile} style={{ fontSize: 12 }} />}
              <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>{uploading ? 'Uploading to Drive…' : 'Filed in the client\'s Drive folder under DME and linked on their Documents tab.'}</div>
            </div>

            <FG label="Notes">
              <textarea value={form.notes} onChange={e => set('notes')(e.target.value)}
                style={{ ...inputStyle, minHeight: 56, resize: 'vertical', lineHeight: 1.5 }} />
            </FG>

            {saveError && <div style={{ fontSize: 12, color: 'var(--danger)', background: 'var(--danger-bg)', padding: '8px 12px', borderRadius: 7 }}>{saveError}</div>}
          </div>
          <div style={{ padding: '14px 24px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Create CMN'}</button>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
